/**
 * Single source of truth for all work-line-specific configuration.
 * Import from @cdc-sccr/shared to use in API, web and mobile.
 */

// ─── Milestone keys ───────────────────────────────────────────────────────────

export type MilestoneKey =
  | "originArrivalAt"
  | "originLoadingAt"
  | "originDepartureAt"
  | "customsOriginArrivalAt"
  | "customsOriginDepartureAt"
  | "borderOriginArrivalAt"
  | "borderOriginDepartureAt"
  | "borderCrossingAt"
  | "borderDestinationArrivalAt"
  | "borderDestinationDepartureAt"
  | "customsDestinationArrivalAt"
  | "customsDestinationDepartureAt"
  | "destinationArrivalAt"
  | "destinationUnloadingAt"

export interface MilestoneDef {
  key:   MilestoneKey
  label: string
  /** Short label used in compact kanban column headers */
  short: string
}

// ─── Batea-specific option types ─────────────────────────────────────────────

export interface BateaOption {
  slug:  string
  label: string
}

// ─── Batea options (editable per-shipment; stored as slug in DB) ──────────────

/** Available loading-window time slots for batea shipments. */
export const BATEA_LOADING_WINDOWS: BateaOption[] = [
  { slug: "00-00-03-30", label: "00:00 – 03:30" },
  { slug: "04-00-07-30", label: "04:00 – 07:30" },
  { slug: "08-00-11-30", label: "08:00 – 11:30" },
  { slug: "12-00-15-30", label: "12:00 – 15:30" },
  { slug: "16-00-19-30", label: "16:00 – 19:30" },
  { slug: "20-00-23-30", label: "20:00 – 23:30" },
]

/** @deprecated Final destinations are now stored in the DB (final_destinations table).
 * Use the API endpoint GET /api/batea/final-destinations instead. */
export const BATEA_FINAL_DESTINATIONS: BateaOption[] = []

// ─── Milestones per work line ─────────────────────────────────────────────────

export const NACIONAL_MILESTONES: MilestoneDef[] = [
  { key: "originArrivalAt",        label: "Llegada al origen",   short: "Llegada origen" },
  { key: "originLoadingAt",        label: "Carga en origen",     short: "Carga" },
  { key: "originDepartureAt",      label: "Salida de origen",    short: "Salida origen" },
  { key: "destinationArrivalAt",   label: "Llegada al destino",  short: "Llegada destino" },
  { key: "destinationUnloadingAt", label: "Descarga en destino", short: "Descarga" },
]

export const INTERNACIONAL_MILESTONES: MilestoneDef[] = [
  { key: "originArrivalAt",               label: "Llegada al origen",          short: "Llegada origen" },
  { key: "originLoadingAt",               label: "Carga en origen",            short: "Carga" },
  { key: "originDepartureAt",             label: "Salida de origen",           short: "Salida origen" },
  { key: "customsOriginArrivalAt",        label: "Llegada aduana origen",      short: "Aduana orig." },
  { key: "customsOriginDepartureAt",      label: "Salida aduana origen",       short: "Sal. aduana O." },
  { key: "borderOriginArrivalAt",         label: "Llegada frontera origen",    short: "Frontera orig." },
  { key: "borderOriginDepartureAt",       label: "Salida frontera origen",     short: "Sal. front. O." },
  { key: "borderCrossingAt",              label: "Cruce de frontera",          short: "Cruce" },
  { key: "borderDestinationArrivalAt",    label: "Llegada frontera destino",   short: "Frontera dest." },
  { key: "borderDestinationDepartureAt",  label: "Salida frontera destino",    short: "Sal. front. D." },
  { key: "customsDestinationArrivalAt",   label: "Llegada aduana destino",     short: "Aduana dest." },
  { key: "customsDestinationDepartureAt", label: "Salida aduana destino",      short: "Sal. aduana D." },
  { key: "destinationArrivalAt",          label: "Llegada al destino",         short: "Llegada destino" },
]

// ─── Journey stages (days elapsed between two milestones) ────────────────────

export interface JourneyStageDef {
  key:   string
  label: string
  from:  MilestoneKey
  to:    MilestoneKey | "finalizedAt"
}

export const INTERNACIONAL_STAGES: JourneyStageDef[] = [
  { key: "loading",           label: "Carga",            from: "originArrivalAt",            to: "customsOriginDepartureAt" },
  { key: "borderOrigin",      label: "Frontera origen",  from: "borderOriginArrivalAt",      to: "borderOriginDepartureAt" },
  { key: "borderDestination", label: "Frontera destino", from: "borderDestinationArrivalAt", to: "customsDestinationDepartureAt" },
  { key: "unloading",         label: "Descarga",         from: "destinationArrivalAt",       to: "finalizedAt" },
]

/** Batea uses loadingWindow + finalDestination instead of journey milestones. */
export const BATEA_MILESTONES: MilestoneDef[] = []

// ─── Work-line display config ─────────────────────────────────────────────────

export const WORK_LINE_LABELS: Record<string, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
}

export const WORK_LINE_COLORS: Record<string, string> = {
  nacional:      "bg-blue-100 text-blue-700",
  internacional: "bg-purple-100 text-purple-700",
  batea:         "bg-amber-100 text-amber-700",
}
